import {
  DEFAULT_FULL_TIME_HOURS_PER_WEEK,
  DEFAULT_WORKING_DAYS_PER_WEEK,
  weeklyTargetHours,
  type EmploymentRow,
} from '@zag/timetracking/common';

export { DEFAULT_FULL_TIME_HOURS_PER_WEEK, DEFAULT_WORKING_DAYS_PER_WEEK };

/**
 * Count the working days encoded in a workday bitmask (bit 0 = Mon … bit 6 =
 * Sun). Returns the popcount of the low seven bits — e.g. Mon–Fri (`31`) → 5,
 * Mon/Wed/Fri (`21`) → 3.
 */
export function countWorkdayBits(mask: number): number {
  let count = 0;
  for (let bit = 0; bit < 7; bit++) {
    if ((mask & (1 << bit)) !== 0) count++;
  }
  return count;
}

/**
 * Whether the given ISO weekday (`0 = Mon … 6 = Sun`) is a working day in the
 * workday bitmask. The bit index equals the ISO weekday index, so this is a
 * direct bit test. Used to disable a person's non-working day cells in the
 * planning grid.
 */
export function isWorkdayInMask(mask: number, isoWeekday: number): boolean {
  if (isoWeekday < 0 || isoWeekday > 6) return false;
  return (mask & (1 << isoWeekday)) !== 0;
}

/** Workday bitmask with every weekday (Mon–Sun) set. */
export const FULL_WEEK_WORKDAY_MASK = 0b1111111;

/**
 * Check if assigning additional hours would exceed the weekly cap.
 *
 * @param alreadyPlannedHours - Hours already planned for the employee this week
 * @param additionalHours - Hours to add (e.g., shift duration)
 * @param targetHoursPerWeek - Employee's weekly cap
 * @returns true if assigning would exceed the cap
 */
export function wouldExceedWeeklyCap(alreadyPlannedHours: number, additionalHours: number, targetHoursPerWeek: number): boolean {
  return alreadyPlannedHours + additionalHours > targetHoursPerWeek;
}

/** Sums each week's target from its own employment rows; flat fallback when there are none. */
export function sumTargetHoursAcrossWeeks(
  rows: readonly EmploymentRow[] | undefined,
  weekStartDates: readonly string[],
  fullTimeHoursPerWeek: number,
  fallbackTargetHoursPerWeek: number
): number {
  if (!rows || rows.length === 0) {
    return fallbackTargetHoursPerWeek * weekStartDates.length;
  }

  return weekStartDates.reduce((sum, weekStart) => sum + weeklyTargetHours(rows, weekStart, fullTimeHoursPerWeek), 0);
}
